import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import { useTheme } from '../../contexts/ThemeContext';
import { borders } from '../tokens/borders';
import { animations } from '../tokens/animations';
import { shadows } from '../tokens/shadows';
import { spacing } from '../tokens/spacing';

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'outline'
  | 'ghost'
  | 'text'
  | 'gradient'
  | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  iconLeft?: React.ReactNode;
  iconRight?: React.ReactNode;
  iconOnly?: boolean;
  fullWidth?: boolean;
  glow?: boolean;
  ripple?: boolean;
}

const Ripple = styled.span<{ x: number; y: number; size: number }>`
  position: absolute;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.6);
  transform: scale(0);
  animation: ripple 0.6s ease-out;
  left: ${props => props.x}px;
  top: ${props => props.y}px;
  width: ${props => props.size}px;
  height: ${props => props.size}px;
  
  @keyframes ripple {
    to {
      transform: scale(4);
      opacity: 0;
    }
  }
`;

const StyledButton = styled.button<{
  variant: ButtonVariant;
  size: ButtonSize;
  $loading: boolean;
  fullWidth: boolean;
  $iconOnly: boolean;
  $glow: boolean;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${props => (props.size === 'sm' ? spacing[1] : spacing[2])};
  font-family: ${props =>
    props.theme.typography?.fontFamily?.primary || 'inherit'};
  font-weight: 600;
  border: none;
  border-radius: ${borders.radius.md};
  cursor: ${props =>
    props.$loading || props.disabled ? 'not-allowed' : 'pointer'};
  transition: ${animations.transition(
    ['background-color', 'color', 'transform', 'box-shadow'],
    animations.duration.normal,
    animations.easing.easeOut
  )};
  position: relative;
  overflow: hidden;
  width: ${props => (props.fullWidth ? '100%' : props.$iconOnly ? 'auto' : 'auto')};
  
  ${props => props.$iconOnly && css`
    aspect-ratio: 1;
    padding: ${props.size === 'sm' ? spacing[2] : props.size === 'lg' ? spacing[4] : spacing[3]} !important;
  `}

  /* Size variants - md/lg use min-height 44px for touch targets */
  ${props => {
    switch (props.size) {
      case 'sm':
        return `
          padding: 8px 16px;
          font-size: 14px;
          line-height: 1.5;
        `;
      case 'lg':
        return `
          padding: 16px 32px;
          font-size: 18px;
          line-height: 1.5;
          min-height: 44px;
        `;
      default: // md
        return `
          padding: 12px 24px;
          font-size: 16px;
          line-height: 1.5;
          min-height: 44px;
        `;
    }
  }}

  /* Variant styles */
  ${props => {
    const { colors } = props.theme;
    switch (props.variant) {
      case 'primary':
        return css`
          background: ${colors.primary};
          color: ${colors.surface};
          box-shadow: ${shadows.sm};
          &:hover:not(:disabled) {
            background: ${colors.primaryDark};
            ${props.$glow ? `box-shadow: ${shadows.glow.md(colors.primary)};` : ''}
          }
          &:active:not(:disabled) {
            transform: translateY(0);
          }
          &:focus-visible {
            outline: none;
            box-shadow: 0 0 0 2px ${colors.surface}, 0 0 0 4px ${colors.primary};
          }
        `;
      case 'gradient':
        return css`
          background: ${colors.gradientPrimary};
          color: ${colors.surface};
          box-shadow: ${shadows.colored.md(colors.primary)};
          &:hover:not(:disabled) {
            transform: translateY(-2px);
            box-shadow: ${shadows.colored.xl(colors.primary)};
            ${props.$glow ? `box-shadow: ${shadows.glow.lg(colors.primary)}, ${shadows.colored.xl(colors.primary)};` : ''}
          }
          &:active:not(:disabled) {
            transform: translateY(0);
          }
          &:focus-visible {
            outline: none;
            box-shadow: ${shadows.focus(colors.primary)};
          }
        `;
      case 'secondary':
        return css`
          background: ${colors.secondary};
          color: ${colors.surface};
          box-shadow: ${shadows.colored.sm(colors.secondary)};
          &:hover:not(:disabled) {
            background: ${colors.secondaryDark};
            transform: translateY(-2px);
            box-shadow: ${shadows.colored.md(colors.secondary)};
            ${props.$glow ? `box-shadow: ${shadows.glow.sm(colors.secondary)}, ${shadows.colored.md(colors.secondary)};` : ''}
          }
          &:focus-visible {
            outline: none;
            box-shadow: ${shadows.focus(colors.secondary)};
          }
        `;
      case 'outline':
        return css`
          background: transparent;
          color: ${colors.primary};
          border: 2px solid ${colors.primary};
          &:hover:not(:disabled) {
            background: ${colors.primary};
            color: ${colors.surface};
            transform: translateY(-2px);
            box-shadow: ${shadows.colored.md(colors.primary)};
          }
          &:focus-visible {
            outline: none;
            box-shadow: ${shadows.focus(colors.primary)};
          }
        `;
      case 'ghost':
        return css`
          background: transparent;
          color: ${colors.primary};
          &:hover:not(:disabled) {
            background: ${colors.overlayLight};
            transform: translateY(-1px);
          }
          &:focus-visible {
            outline: none;
            box-shadow: ${shadows.focus(colors.primary)};
          }
        `;
      case 'text':
        return css`
          background: transparent;
          color: ${colors.primary};
          padding: ${props.size === 'sm' ? `${spacing[1]} ${spacing[2]}` : props.size === 'lg' ? `${spacing[2]} ${spacing[4]}` : `${spacing[1]} ${spacing[3]}`};
          &:hover:not(:disabled) {
            color: ${colors.primaryDark};
            background: ${colors.overlayLight};
          }
          &:focus-visible {
            outline: none;
            box-shadow: ${shadows.focus(colors.primary)};
          }
        `;
      case 'danger':
        return css`
          background: ${colors.error};
          color: ${colors.surface};
          box-shadow: ${shadows.colored.md(colors.error)};
          &:hover:not(:disabled) {
            background: ${colors.errorDark || colors.error};
            transform: translateY(-2px);
            box-shadow: ${shadows.colored.lg(colors.error)};
            ${props.$glow ? `box-shadow: ${shadows.glow.md(colors.error)}, ${shadows.colored.lg(colors.error)};` : ''}
          }
          &:active:not(:disabled) {
            transform: translateY(0);
          }
          &:focus-visible {
            outline: none;
            box-shadow: ${shadows.focus(colors.error)};
          }
        `;
      default:
        return '';
    }
  }}

  /* Disabled state */
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none !important;
  }

  /* Loading state */
  ${props =>
    props.$loading &&
    `
    pointer-events: none;
    opacity: 0.8;
  `}
`;

const LoadingSpinner = styled.div<{ size: ButtonSize }>`
  width: ${props => props.size === 'sm' ? '14px' : props.size === 'lg' ? '20px' : '16px'};
  height: ${props => props.size === 'sm' ? '14px' : props.size === 'lg' ? '20px' : '16px'};
  border: 2px solid currentColor;
  border-top-color: transparent;
  border-radius: 50%;
  animation: ${animations.keyframes.spin} ${animations.duration.slow}ms
    ${animations.easing.linear} infinite;
`;

export const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  loading = false,
  iconLeft,
  iconRight,
  iconOnly = false,
  fullWidth = false,
  glow = false,
  ripple = false,
  children,
  disabled,
  onClick,
  ...props
}) => {
  const theme = useTheme();
  const [ripples, setRipples] = useState<Array<{ x: number; y: number; size: number; id: number }>>([]);
  const [rippleId, setRippleId] = useState(0);

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (ripple && !disabled && !loading) {
      const button = e.currentTarget;
      const rect = button.getBoundingClientRect();
      const size = Math.max(rect.width, rect.height);
      const x = e.clientX - rect.left - size / 2;
      const y = e.clientY - rect.top - size / 2;
      
      const newRipple = { x, y, size, id: rippleId };
      setRipples([...ripples, newRipple]);
      setRippleId(rippleId + 1);
      
      setTimeout(() => {
        setRipples(prev => prev.filter(r => r.id !== newRipple.id));
      }, 600);
    }
    
    onClick?.(e);
  };

  return (
    <StyledButton
      variant={variant}
      size={size}
      $loading={loading}
      fullWidth={fullWidth}
      $iconOnly={iconOnly}
      $glow={glow}
      disabled={disabled || loading}
      onClick={handleClick}
      {...props}
    >
      {ripples.map(ripple => (
        <Ripple key={ripple.id} x={ripple.x} y={ripple.y} size={ripple.size} />
      ))}
      {loading && <LoadingSpinner size={size} />}
      {!loading && iconLeft && <span>{iconLeft}</span>}
      {!iconOnly && children}
      {!loading && iconRight && <span>{iconRight}</span>}
    </StyledButton>
  );
};
